import type { AskTurnView } from '@/state/learnings/learnings-ask-thread';
import type { LearningsAskManager } from '@/state/learnings/useLearningsAsk';
import { RotateCcw } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { AiMessage, ChatInputBox, ChatLogFrame, ChatPanelFrame, ChatPanelHeader } from '@/components/ai-chat';
import { FirstUseNotice, SenderRow, TermsAndPrivacyLinks, TextButton, UserMessage } from '@/components/ai-chat';
import { WorkingSteps } from '@/components/ai-chat';
import { formatCredits } from 'helpers/ai-credits';
import { cn } from 'helpers/cn';
import { staleLine } from '@/state/learnings/learnings-ask-thread';
import {
  LEARNINGS_ASK_INPUT_ID,
  LEARNINGS_ASK_OUT_OF_CREDITS_ID,
  SUGGESTED_QUESTIONS,
} from '@/state/learnings/useLearningsAsk';
import { senderName, workingPhase } from '@/state/results-ai/results-ai-thread';
import { WorkspaceSidePanel } from '../../DashboardLayout/WorkspacePanel';
import { FOCUS_RING } from '../../ConsolePanel';
import { AnswerMarkdown } from '../Details/ResultsAi/AnswerMarkdown';

/** The field's words, as the results chat's title is its field's (renamed 2026-10-20). */
export const LEARNINGS_ASK_TITLE = 'Ask about your learnings';

export const GREETING =
  'Ask me anything about what this website’s tests show: what worked, where, and what to try next. I read the same learnings, patterns and opportunities you see, and any test’s results when a question needs them.';

type OpenTest = (experimentId: number) => void;

/** The server's step while the results tool runs: "Reading the results of #5871" (ask-run-turn.ts). */
const READING_RESULTS_PREFIX = 'Reading the results of';

type LearningsAskPanelProps = {
  manager: LearningsAskManager;
  /** Opens a cited test's debrief, beside the panel. */
  onOpenTest: OpenTest;
};

/**
 * Ask on the Learnings page, docked beside it as the results chat is beside an experiment (decided
 * 2026-10-20, option B1): the same frame, header, log, streamed answer and input
 * (src/workspaces/components/ai-chat, ResultsAiPanel), so the two read as one assistant. Opening
 * moves focus to the input (AskInput, which mounts only once the side slot exists).
 */
export const LearningsAskPanel = ({ manager, onOpenTest }: LearningsAskPanelProps) => (
  <WorkspaceSidePanel>
    <ChatPanelFrame
      label={LEARNINGS_ASK_TITLE}
      markerAttribute="data-learnings-ask-panel"
      edge="rounded-panel border shadow-lg xl:shadow-none"
      header={
        <ChatPanelHeader
          title={LEARNINGS_ASK_TITLE}
          state={manager.phase ?? 'idle'}
          closeLabel="Close Ask about your learnings"
          onClose={() => manager.setOpen(false)}
        />
      }
      log={<AskLog manager={manager} onOpenTest={onOpenTest} />}
      footer={<AskInput manager={manager} />}
    />
  </WorkspaceSidePanel>
);

type LogProps = { manager: LearningsAskManager; onOpenTest: OpenTest };

const AskLog = ({ manager, onOpenTest }: LogProps) => {
  const { turns, acknowledged } = manager;
  const endRef = useRef<HTMLDivElement>(null);
  const last = turns.at(-1);
  const scrollKey = `${turns.length}:${last?.phase}:${last?.steps.length}:${last?.answer.length}`;

  // Keeps the newest words in view as the answer streams. Scroll sync, not data fetching.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [scrollKey]);

  return (
    <ChatLogFrame>
      {manager.isLoading && <p className="text-micro text-dim">Loading the conversation…</p>}
      {manager.isError && (
        <AiMessage>
          <p role="alert">The conversation could not be loaded.</p>
          <TextButton onClick={manager.reload}>Try again</TextButton>
        </AiMessage>
      )}
      {acknowledged ? (
        <AiMessage>
          <p>{GREETING}</p>
          {turns.length === 0 && !manager.isLoading && (
            <div className="mt-2 flex flex-col gap-1.5">
              {SUGGESTED_QUESTIONS.map((question) => (
                <TextButton key={question} onClick={() => manager.ask(question)}>
                  {question}
                </TextButton>
              ))}
            </div>
          )}
        </AiMessage>
      ) : (
        !manager.isLoading && (
          <AiMessage>
            <FirstUseNotice onAcknowledge={() => void manager.acknowledge()}>
              Before you start: the AI assistant is covered by our <TermsAndPrivacyLinks />. What you type, this
              website&apos;s learnings, patterns and opportunities, and the results of any test it reads, are sent to
              our AI provider to answer you. The conversation is saved with the website for 12 months, and everyone who
              can open its Learnings can read it. Each answer uses AI credits.
            </FirstUseNotice>
            {manager.noticeError && (
              <p role="alert" className="mt-2 text-micro text-danger">
                {manager.noticeError}
              </p>
            )}
          </AiMessage>
        )
      )}
      {turns.map((turn) => (
        <AskTurnMessage key={turn.key} turn={turn} manager={manager} onOpenTest={onOpenTest} />
      ))}
      <AnswerAnnouncer turn={last} />
      <div ref={endRef} />
    </ChatLogFrame>
  );
};

type TurnViewProps = { turn: AskTurnView; manager: LearningsAskManager; onOpenTest: OpenTest };

/** One question and its answer, or where it has got to. Exported for the screen tests. */
export const AskTurnMessage = ({ turn, manager, onOpenTest }: TurnViewProps) => {
  const phase = workingPhase(turn);
  const stale = turn.phase === 'done' ? staleLine(turn.testsReadSince) : null;
  const footnote = turn.phase !== 'working' && turn.credits !== undefined ? formatCredits(turn.credits) : undefined;
  const ids = new Set(turn.citations);
  // Reading a test's results can follow some written text, so its step shows then too.
  const reading = turn.phase === 'working' && !!turn.steps.at(-1)?.startsWith(READING_RESULTS_PREFIX);
  return (
    <>
      {/* The conversation is the website's, shared by everyone who can open its Learnings. */}
      {turn.sender && <SenderRow user={turn.sender} name={senderName(turn.sender)} />}
      <UserMessage text={turn.question} />
      <AiMessage barsState={phase ?? 'idle'} footnote={footnote}>
        {turn.answer && <AnswerMarkdown text={turn.answer} testLinks={{ ids, onOpen: onOpenTest }} />}
        {turn.phase !== 'working' && <Citations ids={turn.citations} onOpenTest={onOpenTest} />}
        {stale && (
          <p data-learnings-ask-stale="" className="mt-2 text-micro text-dim">
            {stale}
          </p>
        )}
        {(phase === 'thinking' || reading) && (
          <WorkingSteps
            steps={turn.steps.length > 0 ? turn.steps : ['Reading this website’s learnings']}
            phase="thinking"
            live={turn.credits !== undefined ? formatCredits(turn.credits) : undefined}
          />
        )}
        {turn.phase === 'stopped' && <p className="mt-2 text-micro text-dim">{manager.stoppedMessage}</p>}
        {turn.phase === 'errored' && turn.error && <TurnError turn={turn} manager={manager} />}
      </AiMessage>
    </>
  );
};

const CITE = 'micro-label rounded-pill border border-line px-2 py-1 text-dim hover:text-foreground';

type CitationsProps = { ids: number[]; onOpenTest: OpenTest };

/** The tests the answer names, checked by the server: each opens its debrief. */
const Citations = ({ ids, onOpenTest }: CitationsProps) =>
  ids.length === 0 ? null : (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="micro-label">Based on</span>
      {ids.map((id) => (
        <button key={id} type="button" onClick={() => onOpenTest(id)} className={cn(CITE, FOCUS_RING)}>
          #{id}
        </button>
      ))}
    </div>
  );

type TurnErrorProps = { turn: AskTurnView; manager: LearningsAskManager };

const TurnError = ({ turn, manager }: TurnErrorProps) => {
  const isLatest = manager.turns.at(-1)?.key === turn.key;
  return (
    <div className={turn.answer ? 'mt-2' : undefined}>
      <p role="alert">{turn.error?.message}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {turn.error?.status === 402 && manager.billingUrl && (
          <NavLink to="/settings/billing" className="text-micro font-medium text-accent hover:underline">
            Buy a credit pack
          </NavLink>
        )}
        {turn.error?.retryable && isLatest && (
          <TextButton onClick={() => manager.retry(turn.key)}>
            <RotateCcw aria-hidden="true" className="size-3" />
            Try again
          </TextButton>
        )}
      </div>
    </div>
  );
};

type AnswerAnnouncerProps = { turn: AskTurnView | undefined };

/** Says once that an answer is ready; the log itself is silent, as the results chat's is. */
const AnswerAnnouncer = ({ turn }: AnswerAnnouncerProps) => {
  const message =
    turn?.phase === 'done' ? 'Answer ready' : turn?.phase === 'errored' ? 'The answer could not be finished' : '';
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {message}
    </p>
  );
};

type InputProps = { manager: LearningsAskManager };

const AskInput = ({ manager }: InputProps) => {
  const { acknowledged } = manager;

  // Focus management on open, not data fetching, as the results chat's input does: here because
  // this mounts inside the side slot's portal. Before the notice is accepted its OK takes focus.
  useEffect(() => {
    const input = document.getElementById(LEARNINGS_ASK_INPUT_ID) as HTMLTextAreaElement | null;
    const target =
      input && !input.disabled
        ? input
        : document.querySelector<HTMLElement>('[data-learnings-ask-panel] [data-ai-focus]');
    target?.focus({ preventScroll: true });
  }, []);

  return (
    <ChatInputBox
      inputId={LEARNINGS_ASK_INPUT_ID}
      label={LEARNINGS_ASK_TITLE}
      value={manager.draft}
      onChange={manager.setDraft}
      onKeyDown={manager.onInputKeyDown}
      placeholder={acknowledged ? 'Ask about your learnings…' : 'Press OK on the notice above to start'}
      disabled={!acknowledged}
      busy={manager.busy}
      phase={manager.phase}
      canSend={manager.canSend}
      onSend={manager.submit}
      onStop={manager.stop}
      status={manager.statusLine}
      statusAlert={manager.outOfCredits ? { id: LEARNINGS_ASK_OUT_OF_CREDITS_ID, text: 'Out of credits' } : undefined}
      meter={manager.meter}
    />
  );
};
